import _ from 'lodash'
import { safeAwait } from '@vzmi/component_ec_nodejs_util/dist/lib/FetchDataUtil'
import { throwIfError } from '../../../../../lib/reserviceErrorHelpers'
import {
  getCustomError,
  getDataFromHtml,
  getSafeCopy,
  getExternalLabel,
  isRegulatoryCertificationAttributeLabel,
  appendProductImageHash
} from '../helpers'
import useCorsCheck from './useCorsCheck'
import useCloneRequestController from './useCloneRequestController'
import useBrands from './useBrands'
import useCategoryAttributes from './useCategoryAttributes'
import useExternalHtml from './useExternalHtml'
import { CAUSE_CODE, SCHEMA } from '../constants'

/**
 * Returns the full Momo schema with copy field that uses the injected fetch.
 * On fetch error returns ''; on success returns full HTML text.
 * @param {Object} requestController - From useCloneRequestController
 * @param {Function} getBrandIdByName - From useBrands, resolves brand name to id
 * @param {Function} [match] - From useCategoryAttributes, (attr) => boolean
 * @returns {Object} Schema for getDataFromHtml
 */
export const getMomoSchema = (
  { fetchWithAbort },
  { getBrandIdByName, match }
) => ({
  [SCHEMA.TITLE]: {
    type: 'string',
    selector: 'meta[property="og:title"]',
    attribute: 'content'
  },
  [SCHEMA.BRAND]: {
    type: 'custom',
    selector: 'meta[property="product:brand"]',
    attribute: 'content',
    callback: (value = '') => {
      const name = (value || '').trim()
      return { name, id: getBrandIdByName(value) }
    }
  },
  [SCHEMA.ATTRIBUTES_TABLE]: {
    type: 'grabHtml',
    callback: (html = '') => {
      if (typeof html !== 'string' || _.isEmpty(html.trim())) {
        return []
      }

      const rowsFromAppendScript = (() => {
        const appendMatch = html.match(/attributesTable\.append\("(.*)"\)/)
        const tableHtml = _.get(appendMatch, '1')
        if (!tableHtml) {
          return []
        }

        const doc = new DOMParser().parseFromString(tableHtml, 'text/html')
        const attributesTableElement = doc.querySelector('ul li')
        if (!attributesTableElement) {
          return []
        }

        const lines = attributesTableElement.innerHTML
          .replace(/<br\s*\/?>/gi, '\n')
          .split('\n')
          .map((line) => _.trim(line))
          .filter((line) => !_.isEmpty(line))

        const labelValueRegex = /^([^：:]*)([：:])(.*)$/
        const parsed = []
        for (const line of lines) {
          const lineMatch = line.match(labelValueRegex)
          if (!lineMatch) {
            continue
          }

          const label = _.trim(lineMatch[1]).replace(/^\s*-\s*/, '')
          const value = _.trim(lineMatch[3])
          if (_.isEmpty(label) || _.isEmpty(value)) {
            continue
          }

          parsed.push({ label, value })
        }
        return parsed
      })()

      const isNeedsDomFallback = (
        _.isEmpty(rowsFromAppendScript) ||
        (
          _.size(rowsFromAppendScript) === 1 &&
          _.trim(_.get(rowsFromAppendScript, '0.label', '')) === '認證字號'
        )
      )

      const rowsFromDom = isNeedsDomFallback
        ? (() => {
          const pageDoc = new DOMParser().parseFromString(html, 'text/html')
          const table = pageDoc.querySelector('#attributesTable')
          if (!table) {
            return []
          }

          const rows = []
          for (const tr of table.querySelectorAll('tr')) {
            const th = tr.querySelector('th')
            const td = tr.querySelector('td')
            if (!th || !td) {
              continue
            }

            const label = _.trim(_.get(th, 'innerText', '').replace(/\s+/g, ' '))
            const value = _.trim(_.get(td, 'innerText', '').replace(/\s+/g, ' '))
            if (_.isEmpty(label) || _.isEmpty(value)) {
              continue
            }

            rows.push({ label, value })
          }
          return rows
        })()
        : []

      const combined = [...rowsFromDom, ...rowsFromAppendScript]

      const seen = new Set()
      const rows = combined.filter((row) => {
        if (seen.has(row.label)) {
          return false
        }

        seen.add(row.label)
        return true
      })
      return rows
        .filter((row) => {
          return !_.isEmpty(row.label) && !_.isEmpty(row.value)
        })
        .map((row) => ({
          ...row,
          label:
            match(row) && !isRegulatoryCertificationAttributeLabel(row.label)
              ? row.label
              : getExternalLabel(row.label)
        }))
    }
  },
  [SCHEMA.PRODUCT_COPY]: {
    type: 'custom',
    selector: '#ifrmGoods',
    attribute: 'src',
    callback: async (goodsIframeSrc = '') => {
      if (_.isEmpty(goodsIframeSrc)) {
        return ''
      }

      const { pathname } = new URL(goodsIframeSrc)
      const url = `https://www.momoshop.com.tw${pathname}`
      const [fetchError, response] = await safeAwait(fetchWithAbort(url))
      if (fetchError) {
        return ''
      }

      const copy = await response.text()
      return getSafeCopy(copy)
    }
  },
  [SCHEMA.SHORT_DESCRIPTION]: {
    type: 'custom',
    selector: '#categoryActivityInfo',
    attribute: 'innerHTML',
    callback: (html = '') => {
      if (typeof html !== 'string' || _.isEmpty(html.trim())) {
        return []
      }

      const doc = new DOMParser().parseFromString(html, 'text/html')
      const descList = Array.from(doc.querySelectorAll('li:not([class])'))
      return descList
        .map((desc) => (desc ? _.trim(_.get(desc, 'innerText', '')) : ''))
        .filter((desc) => !_.isEmpty(desc))
        .slice(0, 5)
    }
  },
  [SCHEMA.PRODUCT_IMAGES]: {
    type: 'custom',
    selector: '#GoodsDetailPic_S table',
    attribute: 'innerHTML',
    callback: (html = '') => {
      if (typeof html !== 'string' || _.isEmpty(html.trim())) return []
      const doc = new DOMParser().parseFromString(html, 'text/html')
      const images = Array.from(doc.querySelectorAll('img'))
      const numberedVariantRegexp = /_((?![Bb])[A-Za-z])(\d+)\.webp(?=\?|$)/i
      const suffixRegexp = /(.*)(_[a-zA-Z0-9]+)\.webp(?=\?|$)/i
      return images
        .map((image) => {
          const source = image.getAttribute('src')
          if (!source) {
            return ''
          }

          const matchResult = source.match(suffixRegexp)
          if (!matchResult) {
            return ''
          }

          if (numberedVariantRegexp.test(source)) {
            return source.replace(numberedVariantRegexp, '_B$2.webp')
          }

          return source.replace(suffixRegexp, '$1_B.webp')
        })
        .filter((image) => !_.isEmpty(image))
        .map(appendProductImageHash)
        .slice(0, 10)
    }
  },
  [SCHEMA.PRODUCT_SPECS]: {
    type: 'custom',
    selector: 'ul.prdPriceDetail',
    attribute: 'innerHTML',
    callback: (html = '') => {
      if (typeof html !== 'string' || _.isEmpty(html.trim())) return []
      const doc = new DOMParser().parseFromString(html, 'text/html')
      const specs = Array.from(doc.querySelectorAll('li div[name^="spec"]:not([style*="display:none"])'))
      const productSpecs = specs
        .map((row) => {
          if (!row) {
            return null
          }

          const name = _.get(row.querySelector('b'), 'innerText', '')
            .replace(':', '')
            .trim()
          const values = Array.from(
            row.querySelectorAll('ul[id^="spec"] li')
          ).map((e) => _.trim(_.get(e, 'innerText', '')))
          return { name, values }
        })
        .filter((spec) => spec && !_.isEmpty(spec.name) && !_.isEmpty(spec.values))
        .slice(0, 2)
      return productSpecs.map((spec) => {
        const isUnmatch = _.some(spec.values, (value) =>
          !match({ label: spec.name, value }, true)
        )
        return {
          ...spec,
          isUnmatch
        }
      })
    }
  }
})

const useMomoListing = () => {
  const { trigger: corsCheckTrigger } = useCorsCheck()
  const requestController = useCloneRequestController()
  const { getBrandIdByName } = useBrands()
  const { match } = useCategoryAttributes()
  const { fetchHtml } = useExternalHtml()

  const trigger = async (formValues) => {
    const [corsError] = await safeAwait(corsCheckTrigger())
    if (corsError) {
      throw corsError
    }

    const externalUrl = _.get(formValues, 'externalUrl')
    const text = await fetchHtml(externalUrl, {
      causeCode: CAUSE_CODE.EXTERNAL_CLONE_FETCH_MOMO_LISTING_ERROR
    })
    const schema = getMomoSchema(requestController, { getBrandIdByName, match })
    const proposal = await getDataFromHtml(text, schema)
    if (_.isEmpty(_.get(proposal, SCHEMA.TITLE))) {
      const customError = getCustomError(CAUSE_CODE.EXTERNAL_CLONE_BLOCKED_ERROR)
      throwIfError({ httpError: customError })
    }

    return {
      proposal,
      error: null
    }
  }

  return { trigger }
}

export default useMomoListing
